import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  DOCUMENT_CHECK_MESSAGES,
  DOCUMENT_KIND_LABELS,
  DOCUMENT_KINDS,
  formatDay,
  MAX_DOCUMENT_BYTES,
  parseIsoDay,
  sniffContentType,
  type DocumentKind,
} from '@wodi/domain';
import type { PatientDocumentView } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  FileField,
  formatFileSize,
  LoadingBlock,
  Notice,
  Select,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  documentEnteredInError,
  fetchDocumentContent,
  fileToBase64,
  uploadDocument,
} from '../api/clinical-documents.ts';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './documents.module.css';

// Documents uploaded to a visit (docs/plans/phase1-consultation.md §7.4; P10, P11): PDF,
// JPG or PNG up to 10 MB, with what it is, a title and the date on the paper. The server
// decides the type from the bytes; the check here only answers sooner. Opening one is
// audited as a chart read, "Download" as an export. Nothing is deleted: a mistake is
// marked entered in error with a reason and stays in the list.

const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png', '.pdf', '.jpg', '.jpeg', '.png'];
const TITLE_MAX = 200;
const SNIFF_BYTES = 16;

async function checkFile(file: File): Promise<string | null> {
  if (file.size === 0) return DOCUMENT_CHECK_MESSAGES['document.empty'];
  if (file.size > MAX_DOCUMENT_BYTES) return DOCUMENT_CHECK_MESSAGES['document.too_large'];
  const head = new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer());
  return sniffContentType(head) === null
    ? DOCUMENT_CHECK_MESSAGES['document.type_not_allowed']
    : null;
}

function UploadForm({
  visitId,
  listKeys,
  onDone,
}: {
  visitId: string;
  listKeys: readonly QueryKey[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [kind, setKind] = useState<DocumentKind>('external_result');
  const [title, setTitle] = useState('');
  const [documentDate, setDocumentDate] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  const titleError =
    title.trim() === ''
      ? 'Give the document a title, such as the test or the sender.'
      : title.trim().length > TITLE_MAX
        ? `Keep the title to ${String(TITLE_MAX)} characters.`
        : null;
  const missingFile = file === null ? 'Choose the scanned document.' : null;

  const upload = useMutation({
    mutationFn: async () => {
      if (file === null) throw new Error('no file');
      return uploadDocument(visitId, {
        clientRequestId,
        kind,
        title: title.trim(),
        documentDate,
        contentBase64: await fileToBase64(file),
      });
    },
    onSuccess: () => {
      toasts.show({ title: 'Document uploaded' });
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
      onDone();
    },
  });
  const serverError = upload.error instanceof ApiError ? upload.error.message : null;

  return (
    <>
      <div className={styles.fields}>
        <FileField
          label="Document"
          description="PDF, JPG or PNG, up to 10 MB."
          acceptedFileTypes={ACCEPTED}
          file={file}
          onChange={(next) => {
            setFile(next);
            setFileError(null);
            if (next !== null) void checkFile(next).then(setFileError);
          }}
          {...(fileError !== null
            ? { errorMessage: fileError }
            : tried && missingFile !== null
              ? { errorMessage: missingFile }
              : {})}
        />
        <Select
          label="What it is"
          options={DOCUMENT_KINDS.map((id) => ({ id, label: DOCUMENT_KIND_LABELS[id] }))}
          value={kind}
          onChange={(key) => {
            if (key !== null) setKind(String(key) as DocumentKind);
          }}
        />
        <TextField
          label="Title"
          value={title}
          onChange={setTitle}
          isRequired
          {...(tried && titleError !== null ? { errorMessage: titleError } : {})}
        />
        <DateField
          label="Date on the paper (optional)"
          value={documentDate}
          onChange={setDocumentDate}
        />
        {serverError === null ? null : (
          <p className={styles.help} role="alert">
            {serverError}
          </p>
        )}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={upload.isPending}
          pendingLabel="Uploading…"
          onPress={() => {
            setTried(true);
            if (missingFile !== null || fileError !== null || titleError !== null) return;
            upload.mutate();
          }}
        >
          Upload document
        </Button>
      </DialogActions>
    </>
  );
}

/** "Upload document": disabled offline, with the reason, and without an open visit. */
export function UploadDocument({
  visitId,
  listKeys,
  online,
}: {
  visitId: string | null;
  listKeys: readonly QueryKey[];
  online: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        isDisabled={!online || visitId === null}
        onPress={() => {
          setOpen(true);
        }}
      >
        Upload document
      </Button>
      {!online ? (
        <p className={styles.help}>Uploading needs a connection to Wodi.</p>
      ) : visitId === null ? (
        <p className={styles.help}>
          Documents are uploaded to an open visit. Check the patient in first.
        </p>
      ) : null}
      {open && visitId !== null ? (
        <Dialog
          title="Upload document"
          isOpen
          onOpenChange={(next) => {
            if (!next) setOpen(false);
          }}
        >
          <UploadForm
            visitId={visitId}
            listKeys={listKeys}
            onDone={() => {
              setOpen(false);
            }}
          />
        </Dialog>
      ) : null}
    </>
  );
}

const contentKey = (id: string) => ['documents', id, 'content'] as const;

/**
 * The bytes as an object URL of a blob with the stored, allowed type, so the frame can
 * only render them as a PDF or an image. Each open is one audited read; the URL is revoked
 * when the viewer closes (`releaseDocumentUrl`).
 */
function useDocumentUrl(doc: PatientDocumentView) {
  return useQuery({
    queryKey: contentKey(doc.id),
    queryFn: async () =>
      URL.createObjectURL(
        new Blob([await fetchDocumentContent(doc.id, 'view')], { type: doc.contentType }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

function releaseDocumentUrl(queryClient: QueryClient, id: string): void {
  const url = queryClient.getQueryData<string>(contentKey(id));
  if (url !== undefined) URL.revokeObjectURL(url);
  queryClient.removeQueries({ queryKey: contentKey(id) });
}

function DocumentViewer({ doc }: { doc: PatientDocumentView }) {
  const content = useDocumentUrl(doc);
  if (content.isPending) return <LoadingBlock label="Opening the document" />;
  if (content.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void content.refetch()}>Try again</Button>}
      >
        {content.error instanceof ApiError
          ? content.error.message
          : 'The document could not be opened. Try again.'}
      </Notice>
    );
  }
  const url = content.data;
  return doc.contentType === 'application/pdf' ? (
    <iframe className={styles.viewer} src={url} title={doc.title} />
  ) : (
    <img className={styles.viewerImage} src={url} alt={doc.title} />
  );
}

async function download(doc: PatientDocumentView): Promise<void> {
  const blob = await fetchDocumentContent(doc.id, 'download');
  const url = URL.createObjectURL(new Blob([blob], { type: doc.contentType }));
  const link = document.createElement('a');
  link.href = url;
  const extension =
    doc.contentType === 'application/pdf' ? 'pdf' : doc.contentType === 'image/png' ? 'png' : 'jpg';
  link.download = `${doc.title}.${extension}`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function dayWords(text: string | null): string | null {
  if (text === null) return null;
  const day = parseIsoDay(text);
  return day.ok ? formatDay(day.value) : text;
}

export function DocumentList({
  documents,
  listKeys,
  mayCorrect,
  online,
}: {
  documents: readonly PatientDocumentView[];
  listKeys: readonly QueryKey[];
  mayCorrect: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<PatientDocumentView | null>(null);
  const [correcting, setCorrecting] = useState<PatientDocumentView | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const closeViewer = () => {
    if (viewing !== null) releaseDocumentUrl(queryClient, viewing.id);
    setViewing(null);
  };
  const correct = useMutation({
    mutationFn: (reason: string) => {
      if (correcting === null) throw new Error('nothing chosen');
      return documentEnteredInError(correcting.id, reason);
    },
    onSuccess: () => {
      toasts.show({ title: 'Marked entered in error' });
      setCorrecting(null);
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  if (documents.length === 0) return <p className={styles.help}>No documents uploaded yet.</p>;
  return (
    <>
      {downloadError === null ? null : <Notice tone="error">{downloadError}</Notice>}
      <ul className={styles.list} aria-label="Uploaded documents">
        {documents.map((doc) => {
          const paperDate = dayWords(doc.documentDate);
          const error = doc.enteredInError;
          return (
            <li key={doc.id} className={styles.item}>
              <div className={styles.itemMain}>
                <span className={styles.itemTitle}>{doc.title}</span>
                <span className={styles.meta}>
                  {[
                    DOCUMENT_KIND_LABELS[doc.kind],
                    paperDate === null ? 'no date on the paper' : `dated ${paperDate}`,
                    formatFileSize(doc.sizeBytes),
                  ].join(', ')}
                </span>
                <span className={styles.meta}>
                  {`Uploaded ${formatInstant(doc.uploadedAt)} by ${doc.uploadedBy.displayName}, visit `}
                  <span className={styles.mono}>{doc.visitNumber}</span>
                </span>
                {error === null ? null : (
                  <span className={styles.meta}>
                    {`Entered in error ${formatInstant(error.at)} by ${error.by.displayName}: ${error.reason}`}
                  </span>
                )}
              </div>
              {error === null ? null : <Tag state="neutral">Entered in error</Tag>}
              <div className={styles.itemActions}>
                <Button
                  variant="secondary"
                  isDisabled={!online}
                  onPress={() => {
                    setViewing(doc);
                  }}
                >
                  Open
                </Button>
                <Button
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    setDownloadError(null);
                    download(doc).catch((e: unknown) => {
                      setDownloadError(
                        e instanceof ApiError ? e.message : 'The download failed. Try again.',
                      );
                    });
                  }}
                >
                  Download
                </Button>
                {mayCorrect && error === null ? (
                  <Button
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      setCorrecting(doc);
                    }}
                  >
                    Entered in error
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {viewing === null ? null : (
        <Dialog
          title={viewing.title}
          isOpen
          onOpenChange={(open) => {
            if (!open) closeViewer();
          }}
        >
          <DocumentViewer doc={viewing} />
          <DialogActions>
            <Button onPress={closeViewer}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {correcting === null ? null : (
        <ReasonDialog
          title="Mark document entered in error"
          isOpen
          onOpenChange={(open) => {
            if (!open) setCorrecting(null);
          }}
          confirmLabel="Mark entered in error"
          pendingLabel="Saving…"
          isPending={correct.isPending}
          error={correct.error instanceof ApiError ? correct.error.message : null}
          onConfirm={(reason) => {
            correct.mutate(reason);
          }}
        >
          <p className={styles.help}>It stays in the list, marked, with your reason.</p>
        </ReasonDialog>
      )}
    </>
  );
}
